import * as React from "react"
import { usePage } from "@inertiajs/react"
import {
  Banknote,
  Boxes,
  Building2,
  Component,
  Database,
  Factory,
  FileText,
  Gauge,
  Grid3x3,
  Home,
  Inbox,
  Layout,
  LayoutDashboard,
  BadgePercent,
  LineChart,
  Mail,
  MapPinCheck,
  Megaphone,
  Package,
  Palette,
  PieChart,
  ScanSearch,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Store,
  Table2,
  Tags,
  TrendingUp,
  Truck,
  Type,
  Users,
} from "lucide-react"
import { MainNav, type NavEntry } from "@/components/MainNav"
import type { PageProps } from "@/types/inertia"
import { MONTHLY_CATEGORIES, monthlyNavBadge, type MonthlyCategoryKey } from "@/lib/datasetRegistry"
import { cn } from "@/lib/utils"

export function AppShell({
  children,
  full,
  width = "default",
}: {
  children: React.ReactNode
  full?: boolean
  /** Content container width. "wide" (max-w-7xl) suits data-heavy tables; "default" (max-w-4xl) for reading-width pages. */
  width?: "default" | "wide"
}) {
  const { props } = usePage<PageProps>()
  const isAdmin = props.current_user?.admin ?? false
  const unreadCount = props.admin_inbox_unread_count ?? 0
  const integrityMismatchCount = props.data_integrity_mismatch_count ?? 0
  const dqCount = props.product_master_dq_count ?? 0
  const monthlyStatus = props.monthly_upload_status

  // Monthly-tracked upload entries source their label/href/badge from the
  // single monthly registry in datasetRegistry.ts (nav structure stays here).
  const monthly = (key: MonthlyCategoryKey) => ({
    href: MONTHLY_CATEGORIES[key].uploadsHref,
    label: MONTHLY_CATEGORIES[key].label,
    badge: monthlyNavBadge(key, monthlyStatus?.[key]),
  })

  const navItems: NavEntry[] = [
    {
      href: "/dashboard",
      icon: Home,
      label: "Home",
      match: (url: string) => url === "/" || url.startsWith("/dashboard"),
    },
    ...(isAdmin
      ? [
          { type: "separator" as const },
          {
            href: "/admin",
            icon: LayoutDashboard,
            label: "Dashboard",
            match: (url: string) => url === "/admin" || url === "/admin/",
          },
          {
            href: "/admin/users",
            icon: Users,
            label: "Users",
            match: (url: string) => url.startsWith("/admin/users"),
          },
          {
            href: "/admin/inbox",
            icon: Inbox,
            label: "Inbox",
            match: (url: string) => url.startsWith("/admin/inbox"),
            badge: unreadCount > 0 ? String(unreadCount) : undefined,
          },
          {
            href: "/admin/org-chart",
            icon: Users,
            label: "Org Chart",
            match: (url: string) => url.startsWith("/admin/org-chart"),
          },
          {
            href: "/admin/briefs",
            icon: FileText,
            label: "Brief Mingguan",
            match: (url: string) => url.startsWith("/admin/briefs"),
          },
          {
            href: "/admin/manufacturers",
            icon: Factory,
            label: "Manufaktur",
            match: (url: string) => url.startsWith("/admin/manufacturers"),
          },
          {
            href: "/admin/product-master",
            icon: Boxes,
            label: "Produk Master",
            match: (url: string) =>
              url.startsWith("/admin/product-master") ||
              url.startsWith("/admin/product-aliases") ||
              url.startsWith("/admin/product-market-links"),
            badge: dqCount > 0 ? (dqCount > 99 ? "99+" : String(dqCount)) : undefined,
          },
          {
            href: "/admin/product-performance",
            icon: Gauge,
            label: "Performa Produk",
            match: (url: string) => url.startsWith("/admin/product-performance"),
          },
          {
            href: "/admin/sell-in/crosswalk",
            icon: Truck,
            label: "Sell-In Crosswalk",
            match: (url: string) => url.startsWith("/admin/sell-in/crosswalk"),
          },
          {
            href: "/admin/sell-in/drivers",
            icon: TrendingUp,
            label: "Driver Sell-In",
            match: (url: string) => url.startsWith("/admin/sell-in/drivers"),
          },
          {
            href: "/admin/assistant",
            icon: Sparkles,
            label: "Tanya Mata Langit",
            match: (url: string) => url.startsWith("/admin/assistant"),
          },
          {
            type: "group" as const,
            icon: Database,
            label: "Data",
            storageKey: "admin-nav-data-group",
            matchGroup: (url: string) =>
              url.startsWith("/admin/timeseries") ||
              url.startsWith("/admin/master-outlet-dist") ||
              url.startsWith("/admin/master-outlet") ||
              url.startsWith("/admin/outlet-verification") ||
              url.startsWith("/admin/master-product-dist") ||
              url.startsWith("/admin/product/parent-child") ||
              url.startsWith("/admin/b2b-sell-out") ||
              url.startsWith("/admin/trans-dist-so") ||
              url.startsWith("/admin/trans-dist-do") ||
              url.startsWith("/admin/master-rental") ||
              url.startsWith("/admin/master-listing") ||
              url.startsWith("/admin/market-share-b2b") ||
              url.startsWith("/admin/b2b/") ||
              url.startsWith("/admin/niq-fragrances") ||
              url.startsWith("/admin/beauty-promoters") ||
              url.startsWith("/admin/data") ||
              url.startsWith("/admin/pivot"),
            children: [
              {
                href: "/admin/timeseries/uploads",
                icon: Table2,
                label: "Timeseries",
                match: (url: string) => url.startsWith("/admin/timeseries"),
              },
              {
                href: "/admin/price-monitoring/uploads",
                icon: Tags,
                label: "Price Monitoring",
                match: (url: string) => url.startsWith("/admin/price-monitoring"),
              },
              {
                href: "/admin/promo-activity/uploads",
                icon: Megaphone,
                label: "Promo & Activity",
                match: (url: string) => url.startsWith("/admin/promo-activity"),
              },
              {
                href: "/admin/discount-plan/uploads",
                icon: BadgePercent,
                label: "Discount Plan",
                match: (url: string) => url.startsWith("/admin/discount-plan"),
              },
              {
                type: "group" as const,
                icon: TrendingUp,
                label: "NIQ",
                storageKey: "admin-nav-niq-group",
                matchGroup: (url: string) => url.startsWith("/admin/niq-fragrances"),
                children: [
                  {
                    ...monthly("niq_fragrances"),
                    icon: TrendingUp,
                    match: (url: string) =>
                      url.startsWith("/admin/niq-fragrances") &&
                      !url.startsWith("/admin/niq-fragrances/dashboard"),
                  },
                  {
                    href: "/admin/niq-fragrances/dashboard",
                    icon: LineChart,
                    label: "NIQ Dashboard",
                    match: (url: string) => url.startsWith("/admin/niq-fragrances/dashboard"),
                  },
                ],
              },
              {
                type: "group" as const,
                icon: Store,
                label: "Outlet",
                storageKey: "admin-nav-outlet-group",
                matchGroup: (url: string) =>
                  url.startsWith("/admin/master-outlet-dist") ||
                  url.startsWith("/admin/master-outlet") ||
                  url.startsWith("/admin/outlet-verification") ||
                  url.startsWith("/admin/accounts"),
                children: [
                  {
                    ...monthly("master_outlet"),
                    icon: Store,
                    match: (url: string) => url.startsWith("/admin/master-outlet") && !url.startsWith("/admin/master-outlet-dist"),
                  },
                  {
                    href: "/admin/master-outlet-dist/uploads",
                    icon: Store,
                    label: "Master Outlet Dist",
                    match: (url: string) => url.startsWith("/admin/master-outlet-dist"),
                  },
                  {
                    href: "/admin/outlet-verification",
                    icon: MapPinCheck,
                    label: "Geo Verification",
                    match: (url: string) => url.startsWith("/admin/outlet-verification"),
                  },
                  {
                    href: "/admin/accounts",
                    icon: Building2,
                    label: "Account",
                    match: (url: string) =>
                      url.startsWith("/admin/accounts") &&
                      !url.startsWith("/admin/accounts/portfolio") &&
                      !url.startsWith("/admin/accounts/trade-marketing"),
                  },
                  {
                    href: "/admin/accounts/portfolio",
                    icon: LayoutDashboard,
                    label: "Portfolio",
                    match: (url: string) => url.startsWith("/admin/accounts/portfolio"),
                  },
                  {
                    href: "/admin/accounts/trade-marketing",
                    icon: ShoppingCart,
                    label: "Trade Marketing",
                    match: (url: string) => url.startsWith("/admin/accounts/trade-marketing"),
                  },
                ],
              },
              {
                type: "group" as const,
                icon: Package,
                label: "Product",
                storageKey: "admin-nav-product-group",
                matchGroup: (url: string) =>
                  url.startsWith("/admin/master-product-dist") ||
                  url.startsWith("/admin/star-master-product") ||
                  url.startsWith("/admin/product/parent-child"),
                children: [
                  {
                    href: "/admin/star-master-product/uploads",
                    icon: Package,
                    label: "Master Product",
                    match: (url: string) => url.startsWith("/admin/star-master-product"),
                  },
                  {
                    href: "/admin/master-product-dist/uploads",
                    icon: Package,
                    label: "Master Product Dist",
                    match: (url: string) => url.startsWith("/admin/master-product-dist"),
                  },
                  {
                    href: "/admin/product/parent-child",
                    icon: Boxes,
                    label: "Product Parent-Child",
                    match: (url: string) => url.startsWith("/admin/product/parent-child"),
                  },
                ],
              },
              {
                type: "group" as const,
                icon: PieChart,
                label: "B2B Data",
                storageKey: "admin-nav-b2b-data-group",
                matchGroup: (url: string) =>
                  url.startsWith("/admin/b2b/") ||
                  url.startsWith("/admin/b2b-sell-out") ||
                  url.startsWith("/admin/market-share-b2b"),
                children: [
                  {
                    ...monthly("b2b_sell_in"),
                    icon: ShoppingCart,
                    match: (url: string) => url.startsWith("/admin/b2b/sell-in"),
                  },
                  {
                    ...monthly("b2b_sell_out"),
                    icon: ShoppingCart,
                    match: (url: string) => url.startsWith("/admin/b2b-sell-out"),
                  },
                  {
                    ...monthly("b2b_service_level"),
                    icon: TrendingUp,
                    match: (url: string) => url.startsWith("/admin/b2b/service-level"),
                  },
                  {
                    ...monthly("b2b_stock"),
                    icon: Boxes,
                    match: (url: string) => url.startsWith("/admin/b2b/stock"),
                  },
                  {
                    ...monthly("market_share_b2b"),
                    icon: PieChart,
                    match: (url: string) => url.startsWith("/admin/market-share-b2b"),
                  },
                ],
              },
              {
                type: "group" as const,
                icon: TrendingUp,
                label: "Beauty Promoters",
                storageKey: "admin-nav-beauty-promoters-group",
                matchGroup: (url: string) => url.startsWith("/admin/beauty-promoters"),
                children: [
                  {
                    ...monthly("bp_opsc"),
                    icon: TrendingUp,
                    match: (url: string) => url.startsWith("/admin/beauty-promoters/bp-opsc"),
                  },
                  {
                    href: "/admin/beauty-promoters/bp-closing-stock",
                    icon: TrendingUp,
                    label: "BP Closing Stock",
                    match: (url: string) => url.startsWith("/admin/beauty-promoters/bp-closing-stock"),
                  },
                ],
              },
              {
                ...monthly("trans_dist_so"),
                icon: ShoppingCart,
                match: (url: string) => url.startsWith("/admin/trans-dist-so"),
              },
              {
                ...monthly("trans_dist_do"),
                icon: Truck,
                match: (url: string) => url.startsWith("/admin/trans-dist-do"),
              },
              {
                ...monthly("master_rental"),
                icon: Banknote,
                match: (url: string) => url.startsWith("/admin/master-rental"),
              },
              {
                ...monthly("master_listing"),
                icon: Tags,
                match: (url: string) => url.startsWith("/admin/master-listing"),
              },
              {
                href: "/admin/data/ka-profitability/uploads",
                icon: TrendingUp,
                label: "KA Profitability",
                match: (url: string) => url.startsWith("/admin/data/ka-profitability"),
              },
              {
                href: "/admin/pivot",
                icon: Grid3x3,
                label: "Pivot",
                match: (url: string) => url.startsWith("/admin/pivot"),
              },
              {
                href: "/admin/data/integrity",
                icon: ShieldCheck,
                label: "Data Integrity",
                match: (url: string) => url.startsWith("/admin/data/integrity"),
                badge: integrityMismatchCount > 0 ? String(integrityMismatchCount) : undefined,
              },
              {
                href: "/admin/data-xray",
                icon: ScanSearch,
                label: "Data X-Ray",
                match: (url: string) => url.startsWith("/admin/data-xray"),
              },
            ],
          },
          {
            href: "/admin/email-templates",
            icon: Mail,
            label: "Email templates",
            match: (url: string) => url.startsWith("/admin/email-templates"),
          },
          {
            type: "group" as const,
            icon: Palette,
            label: "Design System",
            storageKey: "admin-nav-design-system-group",
            matchGroup: (url: string) => url.startsWith("/admin/design-system"),
            children: [
              {
                href: "/admin/design-system/branding",
                icon: Palette,
                label: "Branding",
                match: (url: string) => url.startsWith("/admin/design-system/branding"),
              },
              {
                href: "/admin/design-system/structure",
                icon: Layout,
                label: "Structure",
                match: (url: string) => url.startsWith("/admin/design-system/structure"),
              },
              {
                href: "/admin/design-system/elements",
                icon: Component,
                label: "Elements",
                match: (url: string) => url.startsWith("/admin/design-system/elements"),
              },
              {
                href: "/admin/design-system/base-styles",
                icon: Type,
                label: "Base styles",
                match: (url: string) => url.startsWith("/admin/design-system/base-styles"),
              },
            ],
          },
        ]
      : []),
  ]

  return (
    <div className="flex min-h-screen bg-page text-ink-body">
      <MainNav items={navItems} />
      <main
        className={cn(
          "min-w-0 flex-1",
          full
            ? "h-screen overflow-hidden pt-16 lg:pt-0"
            : "px-6 pb-24 pt-16 sm:px-10 lg:py-8 lg:pb-8"
        )}
      >
        {full ? (
          children
        ) : (
          <div className={cn("mx-auto", width === "wide" ? "max-w-7xl" : "max-w-4xl")}>{children}</div>
        )}
      </main>
    </div>
  )
}
